import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { GetAttachmentsQuery } from './get-attachments.query';
import { GetAttachmentsResult } from './get-attachments.result';

/**
 * Get attachments use case.
 * Pure business logic - retrieves attachments for a case.
 *
 * No state management or error mapping - component handles all presentation concerns.
 * Follows Clean Architecture: use case orchestrates business logic only.
 */
@Injectable({ providedIn: 'root' })
export class GetAttachmentsUseCase {
  private readonly _repository = inject(CasesRepository);

  /**
   * Executes retrieval of all attachments for a case.
   *
   * Pure use case - no state management or side effects. Calling component
   * is responsible for managing loading states, updating data lists, and
   * presenting results/errors to the user.
   *
   * @param query - Query containing caseId
   * @returns Observable that emits result with attachments array
   *
   * @throws AttachmentError with appropriate type:
   *   - PERMISSION_DENIED: user lacks view permissions
   *   - NOT_FOUND: case doesn't exist
   *   - NETWORK_ERROR: network connectivity issues
   *   - UNKNOWN: unexpected errors
   *
   * Observable behavior:
   *   - Emits once: { attachments: CaseAttachmentEntity[] } with all attachments
   *   - Completes: after successful retrieval
   *   - Errors: if backend fetch fails (throws AttachmentError)
   *
   * Component responsibilities:
   *   - Set loading state before calling execute()
   *   - Replace attachments list in state on success
   *   - Clear loading state in finalize()
   *   - Map errors to user messages via AttachmentErrorMapperService
   *   - Display toast notifications
   */
  public execute(query: GetAttachmentsQuery): Observable<GetAttachmentsResult> {
    return this._repository.getAttachments(query.caseId).pipe(
      map((attachments) => ({ attachments }))
    );
  }
}
